import _ from 'lodash';
import { DATA_BINDING } from '@wix/app-definition-ids';
import * as stateManagement from '#packages/stateManagement';
import * as util from '#packages/util';
import constants from '#packages/constants';
import type { EditorAPI } from '#packages/editorAPI';
import type { AppData } from '@wix/document-services-types';
import experiment from 'experiment';

const { getDeviceType } = stateManagement.leftBar.selectors;

interface Options {
  appDefinitionId: string;
  widget: Record<string, unknown>;
  originInfo: { biData: Record<string, AnyFixMe> };
  page: {
    isHidden: boolean;
  };
  [key: string]: AnyFixMe;
}

function getDashboardUrl(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: unknown,
  options: Options,
) {
  return new Promise((resolve, reject) => {
    const appDefIdFromOptions = options?.appDefinitionId;
    const appDefinitionId = appDefIdFromOptions || appData.appDefinitionId;
    const applicationId = appDefIdFromOptions
      ? editorAPI.dsRead.platform.getAppDataByAppDefId(appDefinitionId)
          ?.applicationId
      : appData.applicationId;

    const isSaved = isSiteSaved(editorAPI);
    const isDraftMode = isSiteInDraftMode(editorAPI);

    const tpaApi = experiment.isOpen('se_appDefIdUpdates')
      ? editorAPI.dsRead.tpaV2
      : editorAPI.dsRead.tpa;

    const onSaveSuccess = () => {
      if (appDefinitionId === DATA_BINDING) {
        resolve(getWixCodeAppInMyAccountUrl(editorAPI));
      } else if (
        // @ts-expect-error
        tpaApi.app.isHybrid(applicationId) ||
        // @ts-expect-error
        tpaApi.app.isDashboardAppOnly(applicationId)
      ) {
        resolve(getAppInMyAccountUrl(editorAPI, appDefinitionId));
      } else {
        reject('The given appDefinitionId does not have an dashboard URL.'); //eslint-disable-line prefer-promise-reject-errors
      }
    };
    if (isSaved && !isDraftMode) {
      onSaveSuccess();
      return;
    }
    editorAPI.saveManager.saveInBackground(
      function () {
        onSaveSuccess();
      },
      reject,
      'getDashboardUrl',
    );
  });
}

function getAscendUpgradeUrl(
  editorAPI: EditorAPI,
  appData: AppData,
  _token: unknown,
  options: Options,
) {
  const isSaved = isSiteSaved(editorAPI);
  const isDraftMode = isSiteInDraftMode(editorAPI);
  if (!isSaved || isDraftMode) {
    throw new Error('Site has never been saved');
  }

  const origin = options?.origin;
  const slug = options?.slug;
  const msid = editorAPI.dsRead.generalInfo.getMetaSiteId();

  return stateManagement.ascend.selectors.getAscendUrl({ msid, slug, origin });
}

function getBusinessManagerUrl(editorAPI: EditorAPI) {
  const isSaved = isSiteSaved(editorAPI);
  const isDraftMode = isSiteInDraftMode(editorAPI);
  if (!isSaved || isDraftMode) {
    throw new Error('Site has never been saved');
  }

  const bizManagerBaseUrl = util.serviceTopology.businessManagerUrl;
  const metaSiteId = editorAPI.dsRead.generalInfo.getMetaSiteId();
  return util.url.joinURL(bizManagerBaseUrl, metaSiteId);
}

function getEditorSessionId() {
  return util.editorSessionUtil.editorSessionId;
}

function getAppInMyAccountUrl(editorAPI: EditorAPI, appDefinitionId: string) {
  const domain = getCurrentDomain();
  return [
    '//',
    domain,
    '/my-account/sites/',
    editorAPI.dsRead.generalInfo.getMetaSiteId(),
    '/app/',
    appDefinitionId,
    '?referralInfo=editor',
  ].join('');
}

function getWixCodeAppInMyAccountUrl(editorAPI: EditorAPI) {
  const domain = getCurrentDomain();
  return [
    '//',
    domain,
    '/my-account/sites/',
    editorAPI.dsRead.generalInfo.getMetaSiteId(),
    '/simple-app',
    '?app=wixcode',
    '&referralInfo=editor_platform',
  ].join('');
}

function getEditorMode(editorAPI: EditorAPI) {
  const state = editorAPI.store.getState();
  return getDeviceType(state);
}

const loadedTime = window.mainLoaded || 0;
const getEditorReadyTimestamp = function () {
  return _.now() - loadedTime;
};

function getCurrentDomain() {
  const domainMatch = window.location.hostname.match(/editor\.(.*)/);

  // If could not extract domain, return default domain
  return domainMatch ? `www.${domainMatch[1]}` : 'www.wix.com';
}

function isSiteSaved(editorAPI: EditorAPI) {
  return (
    !editorAPI.dsRead.generalInfo.isFirstSave() &&
    !editorAPI.dsRead.generalInfo.isDraft()
  );
}

function isSiteInDraftMode(editorAPI: EditorAPI) {
  return editorAPI.dsRead.generalInfo.isDraft();
}

function getEditorReferrer() {
  return window.document.referrer;
}

function getConsentPolicy() {
  return window.consentPolicyManager
    ? window.consentPolicyManager.getCurrentConsentPolicy()
    : undefined;
}

function getCurrentViewport(editorAPI: EditorAPI) {
  const viewMode = editorAPI.dsRead.viewMode.get();

  return {
    type: viewMode,
    range:
      viewMode === editorAPI.dsRead.viewMode.VIEW_MODES.MOBILE
        ? {
            min: 0,
            max: constants.ROOT_COMPS.MOBILE_EDITOR.MOBILE_FRAME_WIDTH,
          }
        : {
            min: constants.ROOT_COMPS.MOBILE_EDITOR.MOBILE_FRAME_WIDTH,
            max: Number.MAX_SAFE_INTEGER,
          },
  };
}

const isSilentInstall = (editorAPI: EditorAPI) =>
  editorAPI.platform.applications.isSilentInstallRunning();

const getAppPremiumUrl = (editorAPI: EditorAPI, appDefinitionId: string) => {
  return util.premiumUtils.getPremiumUrl(editorAPI, {
    appDefinitionId,
  });
};

const getHostType = () => util.bi.getEditorTypeForBI();

export {
  getDashboardUrl,
  getBusinessManagerUrl,
  getEditorSessionId,
  getEditorReadyTimestamp,
  getEditorMode,
  getEditorReferrer,
  getConsentPolicy,
  getAscendUpgradeUrl,
  isSilentInstall,
  getCurrentViewport,
  getAppPremiumUrl,
  getHostType,
};
